import {type FC, useCallback, useEffect, useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import dayjs from 'dayjs';
import {observer} from 'mobx-react';
import styled from 'styled-components';

import type {Props} from './types';

import {checkIsBrowserDateInUsFormat} from '../../utils/datetime';
import {CustomHeader} from './components/CustomHeader';
import {Day} from './components/Day';
import {web} from './styles';

const weekStart = checkIsBrowserDateInUsFormat() ? 0 : 1;

const Container = styled.div<{scheme: Props['scheme']}>`
  ${web};
`;

export const CalendarDatePicker: FC<Props> = observer(
  ({
    markedDates,
    max,
    min,
    onChange,
    onMonthChange,
    scheme = 'primary',
    showTimeInput = false,
    testID,
    value,
  }) => {
    const [localValue, setLocalValue] = useState(new Date(value));
    const [localMin, setLocalMin] = useState(min ? new Date(min) : undefined);
    const [localMax, setLocalMax] = useState(max ? new Date(max) : undefined);

    useEffect(() => {
      setLocalValue(new Date(value));
    }, [value]);

    useEffect(() => {
      setLocalMin(min ? new Date(min) : undefined);
    }, [min]);

    useEffect(() => {
      setLocalMax(max ? new Date(max) : undefined);
    }, [max]);

    const onChangeCb = useCallback(
      (next: Date) => {
        onChange?.(next.valueOf());
      },
      [onChange],
    );

    const onMonthChangeCb = useCallback(
      (next: Date) => {
        onMonthChange?.(next.valueOf());
      },
      [onMonthChange],
    );

    const renderDayContents = useCallback(
      (day: number, date: Date) => {
        const isSelected = dayjs(date).isSame(localValue, 'day');

        const config = markedDates?.[dayjs(date).format('YYYY-MM-DD')];

        return <Day config={config} day={day} isSelected={isSelected} />;
      },
      [localValue, markedDates],
    );

    return (
      <Container scheme={scheme}>
        <ReactDatePicker
          calendarStartDay={weekStart}
          data-test-id={testID}
          inline
          maxDate={localMax}
          minDate={localMin}
          onChange={onChangeCb}
          onMonthChange={onMonthChangeCb}
          renderCustomHeader={CustomHeader}
          renderDayContents={renderDayContents}
          selected={localValue}
          showTimeInput={showTimeInput}
          value={localValue.toString()}
        />
      </Container>
    );
  },
);
